// src/store/slices/auth/selectors.ts
import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import { getOrganizationIdFromToken } from '@/store/utils/jwtDecoder';

// Base selector
export const selectAuth = (state: RootState) => state.auth;

// Simple selectors (no need for memoization)
export const selectAuthError = createSelector(selectAuth, (authState) => authState.error);
export const selectTwoFactorMethod = createSelector(
  selectAuth,
  (authState) => authState.twoFactorMethod
);
export const selectCurrentUserId = createSelector(
  selectAuth,
  (state: RootState) => state.user.user?.id ?? undefined,
  (authState, userId) => authState.userId ?? userId
);
export const selectCurrentUsername = createSelector(
  selectAuth,
  (state: RootState) => state.user.user?.email ?? '',
  (authState, email) => (authState.username !== '' ? authState.username : email)
);

// Derived / computed selectors (memoized)
export const selectAuthStatus = createSelector(selectAuth, (authState) => authState.status);
// Field-level error selector (factory)
export const selectFieldError = (field: string) =>
  createSelector(selectAuth, (state) => state.fieldErrors[field]);
export const selectFieldErrors = createSelector(selectAuth, (authState) => authState.fieldErrors);

export const selectIsSubmitting = createSelector(selectAuth, (authState) => authState.isSubmitting);

export const selectTwoFactorMaskedDestination = createSelector(
  selectAuth,
  (authState) => authState.twoFactorMaskedDestination
);

export const selectRegToken = createSelector(selectAuth, (authState) => authState.regToken);

export const selectForgotPasswordEmailAddress = createSelector(
  selectAuth,
  (authState) => authState.forgotPasswordEmailAddress
);

export const selectRecoveryToken = createSelector(
  selectAuth,
  (authState) => authState.recoveryToken
);

export const selectRecoveryMaskedPhone = createSelector(
  selectAuth,
  (authState) => authState.recoveryMaskedPhone
);

export const selectSetPasswordStage = createSelector(
  selectAuth,
  (authState) => authState.setPasswordStage
);

export const selectRegistrationOrganizationId = createSelector(selectAuth, (authState) =>
  getOrganizationIdFromToken(authState.registrationToken)
);
